import { Button } from '@progress/kendo-react-buttons';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Input } from '@progress/kendo-react-inputs';
import { Error } from '@progress/kendo-react-labels';
import {
  EquipmentListContext,
  IEquipmentListData,
} from 'app/components/ReactContexts/equipmentListContext';
import { axiosInstance } from 'services/api';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { IEquipmentEntry } from 'types/IEquipmentEntry';
import { tagEvent } from 'utils/ga';
import { formatNumberWithDecimals } from 'utils/general';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import clsx from 'clsx';

interface IEditEngineHours {
  closeDialog: () => void;
}

interface CreateSchemaParams {
  t: (key: string, options?: any) => string;
  hasTelematics?: boolean;
}

export const getEngineHoursSchema = ({
  t,
  hasTelematics,
}: CreateSchemaParams) => {
  return z
    .string()
    .trim()
    .min(1, t('field_is_required'))
    .refine(val => val === '' || /^[0-9.,]+$/.test(val), {
      message: t('engine_hours_incorrect'),
    })
    .transform(val => {
      return val.replace(',', '.');
    })
    .superRefine((val, ctx) => {
      if (val === '') return;

      const customError = {
        code: 'custom',
        message: t('engine_hours_incorrect'),
      } as const;

      const num = Number(val);

      if (isNaN(num)) {
        ctx.addIssue(customError);
        return;
      }

      if (num > 9999999) {
        ctx.addIssue(customError);
        return;
      }

      if (val.includes('.')) {
        const [, decimalPart] = val.split('.');
        const maxDecimals = hasTelematics ? 1 : 2;

        if (decimalPart && decimalPart.length > maxDecimals) {
          ctx.addIssue(customError);
        }
      }
    });
};

export const getEngineHours = (data: IEquipmentListData) => {
  if (
    data.selectedEquipment!.telematics?.cumulativeOperatingHours ||
    data.selectedEquipment!.telematics?.cumulativeOperatingHours == 0
  ) {
    return data.selectedEquipment!.telematics?.cumulativeOperatingHours;
  }
  if (
    data.selectedEquipment!.userEnteredEngineHours ||
    data.selectedEquipment!.userEnteredEngineHours == 0
  ) {
    return data.selectedEquipment!.userEnteredEngineHours;
  }
  return 0;
};

export function EditEngineHours(props: IEditEngineHours) {
  const { t } = useTranslation();

  const { equipmentListData: data, setEquipmentListData: setData } =
    React.useContext(EquipmentListContext);
  const { addErrorModal } = React.useContext(GlobalErrorContext);

  const [isLoading, setIsLoading] = React.useState(false);

  const equipmentData = data.selectedEquipment;
  const hasTelematics = equipmentData?.hasTelematics;

  const schema = React.useMemo(
    () =>
      z.object({
        engineHours: getEngineHoursSchema({ t, hasTelematics }),
      }),
    [t, hasTelematics],
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    values: {
      engineHours: hasTelematics
        ? formatNumberWithDecimals(getEngineHours(data), 1).replace(
            '.',
            t('hours_separator'),
          )
        : formatNumberWithDecimals(getEngineHours(data), 2).replace(
            '.',
            t('hours_separator'),
          ),
    },
  });

  React.useEffect(() => {
    const handlePopState = () => props.closeDialog();

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [getEngineHours(data)]);

  const onSubmit = ({ engineHours }: z.infer<typeof schema>) => {
    setIsLoading(true);
    axiosInstance
      .put(
        `/api/user/equipment/engineHours`,
        JSON.stringify({
          id: equipmentData?.id,
          engineHours: engineHours?.replace(t('hours_separator'), '.'),
        }),
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .then((res: any) => {
        setData(oldData => ({
          ...oldData,
          selectedEquipment: {
            ...(oldData.selectedEquipment as IEquipmentEntry),
            userEnteredEngineHours: Number(
              engineHours?.replace(t('hours_separator'), '.'),
            ),
            dateModified: res.data[0].dateModified,
          },
          equipmentList: oldData.equipmentList!.map((equip, index) => {
            if (index === oldData.selectedIndex) {
              return {
                ...equip,
                userEnteredEngineHours: Number(
                  engineHours?.replace(t('hours_separator'), '.'),
                ),
                dateModified: res.data[0].dateModified,
              };
            }
            return equip;
          }),
        }));
        tagEvent('edit_engine_hours');
        props.closeDialog();
      })
      .catch(e => {
        addErrorModal();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Dialog className="modal-form">
      <div className="modal-container relative">
        <span
          className="back-button absolute"
          onClick={() => {
            props.closeDialog();
          }}
        >
          <svg
            id="ic_nav_back"
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            viewBox="0 0 48 48"
          >
            <rect id="Box" width="48" height="48" fill="none" />
            <path
              id="ic_nav_up"
              d="M16.465,8.482H1.483"
              transform="translate(15.518 15.518)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
            <path
              id="Path_470"
              data-name="Path 470"
              d="M23.676,16.018,16.2,23.748l7.477,7.73"
              transform="translate(0.324 0.252)"
              fill="none"
              stroke="#1a1a1a"
              strokeLinecap="round"
              strokeWidth="2"
            />
          </svg>
        </span>
        <>
          <h2>{t('maintenance_edit_engine_hours')}</h2>
          <div className="form-caption">
            <div className="inline-label-text">
              <span className="label">{t('equipment_model')}</span>
              <span className="text">{equipmentData?.model}</span>
            </div>
            <div className="inline-label-text">
              <span className="label">
                {equipmentData?.identifierType === 'Pin'
                  ? t('equipment_pin')
                  : t('equipment_serial')}
              </span>
              <span className="text">{equipmentData?.pinOrSerial}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div
              className={clsx('form-group', { error: !!errors.engineHours })}
            >
              <div className="form-content">
                <div className="form-label">
                  <span>{t('hours')}</span>
                </div>
                <div className="form-control">
                  <Controller
                    name="engineHours"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder={t('enter_the_new_equipment_hours')}
                        disabled={isLoading}
                      />
                    )}
                  />
                </div>
              </div>

              {errors.engineHours && (
                <div className="form-error">
                  <Error>{errors.engineHours.message}</Error>
                </div>
              )}
            </div>
            <Button
              themeColor="primary"
              type="submit"
              disabled={isLoading || !!errors.engineHours}
            >
              {isLoading ? (
                <div className="loading_icon loading_icon-btn">
                  <div className="css-icon"></div>
                </div>
              ) : (
                t('save_changes')
              )}
            </Button>
          </form>
        </>
      </div>
    </Dialog>
  );
}
